import { useRef, useState, useEffect } from 'react';
import { Play } from 'lucide-react';
import { getVideoEmbedUrl, parseDuration } from '@shared/services/youtube';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';
import type { VideoResult } from '@shared/types/learning-path';

interface VideoEmbedProps {
  video: VideoResult;
  nodeId: string;
  pathId: string;
}

export function VideoEmbed({ video, nodeId, pathId }: VideoEmbedProps) {
  const [loaded, setLoaded] = useState(false);
  const [tracked25, setTracked25] = useState(false);
  // Completion is tracked via a ref (not state) so the timer closure reads the
  // latest value without re-registering; setTracked25(true) also suppresses skip.
  const tracked100Ref = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Clear any pending heuristic timers on unmount. We intentionally read the
  // live timersRef in cleanup so timers added later (in handleLoad) are cleared.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { timersRef.current.forEach(clearTimeout); };
  }, []);

  // Track video skip on unmount if not watched 25%
  useEffect(() => {
    return () => {
      if (loaded && !tracked25) {
        trackEvent({
          name: ANALYTICS_EVENTS.LEARNING_VIDEO_SKIPPED,
          properties: { ...getBaseProperties(), pathId, nodeId, videoId: video.videoId, percentWatched: 0 },
        }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, tracked25]);

  function handleLoad() {
    setLoaded(true);
    // Estimate duration from ISO-8601 (PT#H#M#S); fall back to 180s.
    const durationMatch = video.duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    const totalSeconds = durationMatch
      ? (parseInt(durationMatch[1] ?? '0', 10) * 3600) +
        (parseInt(durationMatch[2] ?? '0', 10) * 60) +
        parseInt(durationMatch[3] ?? '0', 10)
      : 180;

    // Heuristic 1: after 25% of estimated duration, count as "played".
    const quarter = totalSeconds * 0.25 * 1000;
    timersRef.current.push(setTimeout(() => {
      if (!tracked25) {
        setTracked25(true);
        trackEvent({
          name: ANALYTICS_EVENTS.LEARNING_VIDEO_PLAYED,
          properties: { ...getBaseProperties(), pathId, nodeId, videoId: video.videoId, conceptId: '', totalDurationSec: totalSeconds },
        }).catch(() => {});
      }
    }, quarter));

    // Heuristic 2: iframe still mounted at ~90% of duration ≈ watched through.
    // This is the weight-1.0-adjacent "video works for this learner" signal the
    // adaptive engine needs (no YouTube IFrame API wired yet, so this is a proxy).
    const most = totalSeconds * 0.9 * 1000;
    timersRef.current.push(setTimeout(() => {
      if (!tracked100Ref.current) {
        tracked100Ref.current = true;
        setTracked25(true); // suppress skip-on-unmount
        trackEvent({
          name: ANALYTICS_EVENTS.LEARNING_VIDEO_COMPLETED,
          properties: {
            ...getBaseProperties(),
            pathId,
            nodeId,
            videoId: video.videoId,
            percentWatched: 90,
            watchDurationSec: Math.round(totalSeconds * 0.9),
          },
        }).catch(() => {});
      }
    }, most));
  }

  return (
    <div className="rounded-lg bg-dark-surface border border-dark-border overflow-hidden">
      {/* Lazy load: show thumbnail until clicked */}
      {!loaded ? (
        <button
          onClick={() => setLoaded(true)}
          className="relative w-full aspect-video bg-dark-subtle cursor-pointer group"
        >
          <img
            src={video.thumbnailUrl}
            alt={video.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
            <div className="w-14 h-14 rounded-full bg-indigo-500/90 flex items-center justify-center">
              <Play size={24} className="text-white ml-1" />
            </div>
          </div>
        </button>
      ) : (
        <div className="aspect-video">
          <iframe
            ref={iframeRef}
            src={getVideoEmbedUrl(video.videoId)}
            title={video.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onLoad={handleLoad}
          />
        </div>
      )}

      <div className="p-4">
        <p className="text-sm font-body font-medium text-content">{video.title}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-content-muted font-body">
          <span>{video.channelName}</span>
          <span>{parseDuration(video.duration)}</span>
          <span>{(video.viewCount / 1000).toFixed(0)}K views</span>
        </div>
        {video.relevanceNote && (
          <p className="text-xs text-content-dim font-body mt-2 italic">{video.relevanceNote}</p>
        )}
      </div>
    </div>
  );
}
